import { useEffect, useState } from "react";
import type { DetailRow } from "../../api/inv-depth";
import { invPolish, type PickOptions } from "../../api/inv-polish";
import { t } from "../../i18n";

/*
 * The serial and batch picker of one document line (server/inv-doc-tracking.js): shown under a line whose item is tracked by serial
 * number or batch. Goods going out (an invoice, a vendor credit) pick serials and batches that are at the location on the day; goods
 * coming in (a bill) type new ones; a credit note picks from what its invoice took. Nothing shows for other items. The rows go to the
 * server as the line's `tracking`; the server checks them again (and on issue, against the stock that day).
 */

type Props = {
  line: number;
  itemId: string;
  quantity: string;
  direction: "in" | "out";
  locationId?: string | null;
  date?: string | null;
  invoiceId?: string | null;
  value: DetailRow[] | undefined;
  onChange: (rows: DetailRow[]) => void;
};

const units = (q: string) => {
  const n = Number(String(q).replace(/[,\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const sumOf = (rows: DetailRow[]) => rows.reduce((s, r) => s + (r.serial_no ? 1 : units(r.quantity ?? "0")), 0);

export function LineTracking({ line, itemId, quantity, direction, locationId, date, invoiceId, value, onChange }: Props) {
  const [opts, setOpts] = useState<PickOptions | null>(null);
  const [typed, setTyped] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setOpts(null);
    if (!itemId) return;
    invPolish
      .pick({ item_id: itemId, location_id: locationId || null, date: date || null, invoice_id: invoiceId || null })
      .then((o) => live && setOpts(o))
      .catch(() => live && setOpts(null));
    return () => {
      live = false;
    };
  }, [itemId, locationId, date, invoiceId]);
  if (!opts || !opts.tracked || opts.mode === "none") return null;

  const rows = value ?? [];
  const need = units(quantity);
  const have = sumOf(rows);
  const what = opts.mode === "serial" ? t("Serial numbers") : "Batches";
  const short = opts.required && have < need;
  const head = (
    <div className="inv-pick__head">
      <strong>{what}</strong>
      <span className={short ? "inv-pick__short" : "crm-note"} style={{ padding: 0, background: "none" }}>
        {t("{have} of {need} chosen{v}", { have, need, v: opts.required ? t(" · required") : direction === "in" ? t(" · optional") : opts.mode === "serial" ? t(" · optional: the rest go oldest first") : t(" · optional: the rest go first expiry, first out") })}
      </span>
    </div>
  );

  // Serials: tick the ones that leave (or, on a credit note, come back), or type new ones.
  if (opts.mode === "serial") {
    const offered = direction === "out" ? opts.serials.map((s) => ({ no: s.serial_no, note: s.bin ? t("bin {bin}", { bin: s.bin }) : null })) : opts.sold.filter((s) => s.serial_no).map((s) => ({ no: s.serial_no as string, note: t("sold on the invoice") }));
    const chosen = new Set(rows.map((r) => r.serial_no));
    const toggle = (no: string) => onChange(chosen.has(no) ? rows.filter((r) => r.serial_no !== no) : [...rows, { serial_no: no, ...(direction === "out" ? { bin_id: opts.serials.find((s) => s.serial_no === no)?.bin_id ?? null } : {}) }]);
    const text = typed ?? rows.filter((r) => !offered.some((o) => o.no === r.serial_no)).map((r) => r.serial_no).join("\n");
    return (
      <div className="inv-pick" role="group" aria-label={t("Line {line}: {toLowerCase}", { line, toLowerCase: what.toLowerCase() })}>
        {head}
        {offered.length > 0 && (
          <div className="inv-pick__chips">
            {offered.map((o) => (
              <label key={o.no} className={`inv-pick__chip${chosen.has(o.no) ? " is-on" : ""}`}>
                <input type="checkbox" checked={chosen.has(o.no)} disabled={!chosen.has(o.no) && have >= need} onChange={() => toggle(o.no)} />
                {o.no}
                {o.note && <small> · {o.note}</small>}
              </label>
            ))}
          </div>
        )}
        {direction === "out" && offered.length === 0 && <p className="crm-note">{t("No serial numbers of this item are in stock here on that day.")}</p>}
        {direction === "in" && (
          <label className="crm-field">
            <span>{offered.length ? t("Or type them, one per line") : t("Serial numbers, one per line")}</span>
            <textarea
              rows={Math.min(6, Math.max(2, need))}
              value={text}
              onChange={(e) => {
                setTyped(e.target.value);
                const typedNos = e.target.value.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
                onChange([...rows.filter((r) => offered.some((o) => o.no === r.serial_no)), ...typedNos.map((no) => ({ serial_no: no }))]);
              }}
            />
          </label>
        )}
      </div>
    );
  }

  // Batches going out: how many of each (per bin) leave.
  if (direction === "out") {
    const key = (b: string, bin: string | null) => `${b}|${bin ?? ""}`;
    const qty = new Map(rows.map((r) => [key(r.batch_no ?? "", r.bin_id ?? null), r.quantity ?? ""]));
    const set = (b: string, bin: string | null, q: string) => {
      const rest = rows.filter((r) => key(r.batch_no ?? "", r.bin_id ?? null) !== key(b, bin));
      onChange(q.trim() ? [...rest, { batch_no: b, bin_id: bin, quantity: q }] : rest);
    };
    return (
      <div className="inv-pick" role="group" aria-label={t("Line {line}: batches", { line })}>
        {head}
        {opts.batches.length === 0 ? (
          <p className="crm-note">{t("No batches of this item are in stock here on that day.")}</p>
        ) : (
          <table className="inv-pick__table">
            <thead>
              <tr>
                <th>{t("Batch")}</th>
                <th>{t("Expires")}</th>
                <th>{t("Bin")}</th>
                <th className="is-r">{t("There")}</th>
                <th className="is-r">{t("Take")}</th>
              </tr>
            </thead>
            <tbody>
              {opts.batches.flatMap((b) =>
                b.bins.map((p) => (
                  <tr key={key(b.batch_no, p.bin_id)}>
                    <td>{b.batch_no}</td>
                    <td>{b.expiry_date ?? "—"}</td>
                    <td>{p.bin ?? t("No bin")}</td>
                    <td className="is-r num">{p.quantity}</td>
                    <td className="is-r">
                      <input aria-label={t("Line {line}: take from batch {batch_no}", { line, batch_no: b.batch_no })} inputMode="decimal" value={qty.get(key(b.batch_no, p.bin_id)) ?? ""} onChange={(e) => set(b.batch_no, p.bin_id, e.target.value)} />
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // Batches coming in: their numbers, dates, bins and quantities.
  const edit = (i: number, p: Partial<DetailRow>) => onChange(rows.map((r, k) => (k === i ? { ...r, ...p } : r)));
  return (
    <div className="inv-pick" role="group" aria-label={t("Line {line}: batches", { line })}>
      {head}
      {opts.sold.filter((s) => s.batch_no).length > 0 && (
        <p className="crm-note">
          {t("The invoice took{v}", { v: " " })}
          {opts.sold
            .filter((s) => s.batch_no)
            .map((s) => `${s.quantity} of ${s.batch_no}`)
            .join(", ")}
          .
        </p>
      )}
      {rows.map((r, i) => (
        <div className="inv-pick__row" key={i}>
          <label className="crm-field">
            <span>{t("Batch")}</span>
            <input value={r.batch_no ?? ""} maxLength={60} onChange={(e) => edit(i, { batch_no: e.target.value })} />
          </label>
          <label className="crm-field">
            <span>{t("Made")}</span>
            <input type="date" value={r.mfg_date ?? ""} onChange={(e) => edit(i, { mfg_date: e.target.value || null })} />
          </label>
          <label className="crm-field">
            <span>{t("Expires")}</span>
            <input type="date" value={r.expiry_date ?? ""} onChange={(e) => edit(i, { expiry_date: e.target.value || null })} />
          </label>
          {opts.bins.length > 0 && (
            <label className="crm-field">
              <span>{t("Bin")}</span>
              <select value={r.bin_id ?? ""} onChange={(e) => edit(i, { bin_id: e.target.value || null })}>
                <option value="">{t("No bin")}</option>
                {opts.bins.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="crm-field">
            <span>{t("Quantity")}</span>
            <input inputMode="decimal" value={r.quantity ?? ""} onChange={(e) => edit(i, { quantity: e.target.value })} />
          </label>
          <button type="button" className="button button--secondary button--sm" onClick={() => onChange(rows.filter((_, k) => k !== i))}>
            {t("Remove")}
          </button>
        </div>
      ))}
      <button type="button" className="button button--secondary button--sm" style={{ justifySelf: "start" }} onClick={() => onChange([...rows, { batch_no: "", quantity: String(Math.max(0, need - have) || "") }])}>
        {t("Add a batch")}
      </button>
    </div>
  );
}
